import { Tooltip, type TooltipProps } from "@mui/material";
import { useState } from "react";
import { LazyInHouseTooltip } from "./LazyInHouseTooltip";
import { useLazyTooltipScope } from "./lazyTooltipScopeContext";
import { supportsLazyMount } from "./lazyTooltipSupport";

export type InHouseTooltipProps = TooltipProps & {
	/**
	 * Mount MUI's Tooltip only when it first opens, for a tooltip repeated on every row of a dense, virtualized
	 * surface, where mounting one per figure costs every scroll and hardly any is ever opened. The popup, its
	 * delays, placement and Escape are MUI's own; the anchor carries the same listeners and accessible name, and is
	 * never re-mounted. Takes the standard props only (see `supportsLazyMount`). Absent, the nearest
	 * `LazyTooltipScope` decides.
	 */
	lazy?: boolean;
};

export const InHouseTooltip = ({ children, lazy, ...props }: InHouseTooltipProps) => {
	const inLazyScope = useLazyTooltipScope();
	const [open, setOpen] = useState(false);

	if (lazy === true && !supportsLazyMount(props)) {
		throw new Error("InHouseTooltip: `lazy` takes only the standard tooltip props (see supportsLazyMount)");
	}
	// DEFAULT: without its own `lazy`, a tooltip follows the scope it sits in (eager outside any scope); a scope only
	// makes lazy the tooltips that use the standard props.
	if (lazy ?? (inLazyScope && supportsLazyMount(props))) {
		return <LazyInHouseTooltip {...props}>{children}</LazyInHouseTooltip>;
	}
	// `onOpen`/`onClose` aren't lazy props (see `supportsLazyMount`), so a tooltip that passes them always lands here.
	const { onOpen, onClose, ...tooltipProps } = props;
	return (
		<Tooltip
			{...tooltipProps}
			open={open}
			onOpen={(event) => {
				setOpen(true);
				onOpen?.(event);
			}}
			onClose={(event) => {
				setOpen(false);
				onClose?.(event);
			}}
		>
			{children}
		</Tooltip>
	);
};
